'use client';

/**
 * LegalDocViewer — accessible, scrollable markdown viewer for long-form
 * legal/tutorial content rendered inside the seller onboarding wizard
 * (Step 1 lecture + Step 2 T&C) and the legal pages (/legal/*).
 *
 * Design goals:
 *   - Replace the previous `<pre>` monospaced block with a structured
 *     layout: title, "last updated" badge, scrollable body, footer
 *     separator. Typography uses the app's sans-serif stack (not
 *     monospaced) so the text reads like a document, not a console log.
 *   - Sticky header inside the scroll area: title + last-updated badge
 *     remain visible while the user scrolls. Helps the user retain
 *     context (which document they're reading + when it was last
 *     revised) before accepting via the checkbox below.
 *   - Markdown rendering via `react-markdown` (already a dependency).
 *     Supports headings (#/##/###), lists (-/*), tables (| |),
 *     bold (**text**), horizontal rules (---), and inline code.
 *   - Gated behind a `maxHeight` prop (default 32rem ≈ 512px) so the
 *     viewer doesn't push the page footer off-screen on long documents
 *     (the seller T&C is ~3,000 words). The accept checkbox + CTA
 *     button live OUTSIDE the viewer in the parent form so they're
 *     always visible.
 *   - No AI / placeholder / internal-doc references are exposed —
 *     P-083 compliant by construction (the component only renders
 *     whatever string the parent passes; it never injects metadata).
 *
 * Anti-P-044 (ARIA): the scroll region has an explicit aria-label so
 *   screen readers announce it as a "document" region. Headings use
 *   semantic <h1>/<h2>/<h3> (preserved by react-markdown) so the
 *   document has a proper heading outline.
 * Anti-P-042: no console.* statements.
 */

import { type ReactNode, useMemo } from 'react';
import ReactMarkdown from 'react-markdown';
import { Badge } from '@/components/ui/badge';
import { ScrollText } from 'lucide-react';
import { cn } from '@/lib/utils';

export interface LegalDocViewerProps {
  /**
   * The markdown source string. Typically `SELLER_LECTURE_CONTENT`,
   * `SELLER_TERMS_AND_CONDITIONS`, `MUST_READ_LECTURE`, etc.
   */
  content: string;
  /**
   * Accessible label for the scroll region. Should describe the
   * document, e.g. "Términos y Condiciones del Vendedor".
   */
  ariaLabel: string;
  /**
   * Optional title shown in the sticky header. When omitted, the
   * component extracts the first H1 (`# ...`) from the markdown and
   * renders it as the title (the H1 is then removed from the body
   * to avoid duplication).
   */
  title?: string;
  /**
   * Optional "Last updated" date string shown as a badge in the
   * sticky header. When omitted, the component attempts to extract
   * the last line matching `Última actualización: ...` from the
   * markdown footer.
   */
  lastUpdated?: string;
  /**
   * Max height of the scrollable body. Default 32rem (≈ 512px).
   */
  maxHeight?: string;
  /**
   * Optional className appended to the outer container for spacing
   * overrides.
   */
  className?: string;
}

/**
 * Extract the first H1 line (`# Title`) from a markdown string.
 * Returns the title text (without the leading `#`) or null if no
 * H1 is found.
 */
function extractH1(md: string): string | null {
  const match = md.match(/^#\s+(.+)$/m);
  return match ? match[1].trim() : null;
}

/**
 * Remove the first H1 line from the markdown so it isn't rendered
 * twice (once in the sticky header, once in the body). Also trims
 * leading whitespace so the body starts cleanly.
 */
function stripFirstH1(md: string): string {
  return md.replace(/^#\s+.+\n?/m, '').trimStart();
}

/**
 * Extract the "Última actualización: <date>" line from the footer
 * of the markdown. Returns the date string (e.g. "19 de septiembre
 * de 2026") or null if not found.
 */
function extractLastUpdated(md: string): string | null {
  const match = md.match(/Última actualización:\s*(.+)$/m);
  return match ? match[1].trim() : null;
}

/**
 * Strip the "Última actualización: ..." line + the preceding `---`
 * separator from the markdown body (the date is shown as a badge in
 * the sticky header, so duplicating it in the body is noise).
 */
function stripLastUpdatedLine(md: string): string {
  // Remove trailing `---\n\nÚltima actualización: ...\n` (with optional
  // whitespace variations). The pattern is permissive: matches the
  // separator + the date line, anchored at end-of-string.
  return md
    .replace(/\n*---\s*\n+Última actualización:\s*.+\s*$/m, '')
    .trimEnd();
}

export function LegalDocViewer({
  content,
  ariaLabel,
  title,
  lastUpdated,
  maxHeight = '32rem',
  className,
}: LegalDocViewerProps) {
  // Resolve title: explicit prop > first H1 in markdown > empty.
  const resolvedTitle = useMemo(
    () => title ?? extractH1(content) ?? '',
    [title, content],
  );
  // Resolve last-updated: explicit prop > footer line in markdown > null.
  const resolvedLastUpdated = useMemo(
    () => lastUpdated ?? extractLastUpdated(content),
    [lastUpdated, content],
  );
  // Body markdown: strip the H1 (shown in header) + footer date line
  // (shown as badge in header) to avoid duplication.
  const bodyMarkdown = useMemo(() => {
    let body = content;
    if (resolvedTitle) body = stripFirstH1(body);
    if (resolvedLastUpdated) body = stripLastUpdatedLine(body);
    return body;
  }, [content, resolvedTitle, resolvedLastUpdated]);

  return (
    <div
      className={cn(
        'rounded-lg border border-border bg-card overflow-hidden',
        className,
      )}
    >
      {/* ── Sticky header ── */}
      {(resolvedTitle || resolvedLastUpdated) && (
        <div className="flex items-center justify-between gap-3 border-b border-border bg-muted/30 px-4 py-3">
          <div className="flex items-center gap-2 min-w-0">
            <ScrollText className="size-4 text-primary shrink-0" aria-hidden="true" />
            {resolvedTitle && (
              <h2 className="text-sm font-semibold text-foreground truncate">
                {resolvedTitle}
              </h2>
            )}
          </div>
          {resolvedLastUpdated && (
            <Badge variant="outline" className="shrink-0 text-xs font-normal">
              {resolvedLastUpdated}
            </Badge>
          )}
        </div>
      )}

      {/* ── Scrollable body ──
          Bug P-290 (Task ID 36): Radix ScrollArea's Viewport uses
          `height: 100%` (Tailwind `size-full`), which resolves to `auto`
          when the parent Root only declares `max-height` (no explicit
          `height`). As a result, the Viewport grew to its content's
          natural height (~1500-2000px) and the Root's `overflow: hidden`
          clipped it without enabling internal scroll → the user saw the
          first 24rem with NO scrollbar and could not read the rest.
          Fix: replace ScrollArea with a plain `<div>` carrying
          `overflow-y-auto` + the same `maxHeight` inline style. Native
          CSS overflow on a div with max-height DOES constrain the
          scroll-container to maxHeight + activates the scrollbar when
          content exceeds it. `pr-2` keeps the scrollbar from covering
          text. Same `aria-label` + `role="region"` for accessibility. */}
      <div
        className="w-full overflow-y-auto pr-2"
        style={{ maxHeight }}
        aria-label={ariaLabel}
        role="region"
      >
        <div className="px-4 py-4 legal-doc-body">
          <ReactMarkdown
            components={{
              // Map markdown elements to custom classNames for styling.
              h1: ({ children }: { children?: ReactNode }) => (
                <h1 className="text-lg font-bold text-foreground mt-4 mb-3 first:mt-0">
                  {children}
                </h1>
              ),
              h2: ({ children }: { children?: ReactNode }) => (
                <h2 className="text-base font-semibold text-foreground mt-5 mb-2">
                  {children}
                </h2>
              ),
              h3: ({ children }: { children?: ReactNode }) => (
                <h3 className="text-sm font-semibold text-foreground mt-4 mb-2">
                  {children}
                </h3>
              ),
              p: ({ children }: { children?: ReactNode }) => (
                <p className="text-sm leading-relaxed text-foreground/90 mb-3">
                  {children}
                </p>
              ),
              ul: ({ children }: { children?: ReactNode }) => (
                <ul className="text-sm leading-relaxed text-foreground/90 mb-3 ml-4 list-disc space-y-1">
                  {children}
                </ul>
              ),
              ol: ({ children }: { children?: ReactNode }) => (
                <ol className="text-sm leading-relaxed text-foreground/90 mb-3 ml-4 list-decimal space-y-1">
                  {children}
                </ol>
              ),
              li: ({ children }: { children?: ReactNode }) => (
                <li className="text-sm leading-relaxed text-foreground/90">
                  {children}
                </li>
              ),
              strong: ({ children }: { children?: ReactNode }) => (
                <strong className="font-semibold text-foreground">
                  {children}
                </strong>
              ),
              em: ({ children }: { children?: ReactNode }) => (
                <em className="italic text-foreground/90">{children}</em>
              ),
              hr: () => (
                <hr className="border-border my-4" />
              ),
              table: ({ children }: { children?: ReactNode }) => (
                <div className="my-3 overflow-x-auto rounded-md border border-border">
                  <table className="w-full text-xs">
                    {children}
                  </table>
                </div>
              ),
              thead: ({ children }: { children?: ReactNode }) => (
                <thead className="bg-muted/40">{children}</thead>
              ),
              tbody: ({ children }: { children?: ReactNode }) => (
                <tbody className="divide-y divide-border">{children}</tbody>
              ),
              tr: ({ children }: { children?: ReactNode }) => (
                <tr className="odd:bg-muted/20">{children}</tr>
              ),
              th: ({ children }: { children?: ReactNode }) => (
                <th className="px-2.5 py-1.5 text-left font-semibold text-foreground">
                  {children}
                </th>
              ),
              td: ({ children }: { children?: ReactNode }) => (
                <td className="px-2.5 py-1.5 text-foreground/80">
                  {children}
                </td>
              ),
              a: ({ href, children }: { href?: string; children?: ReactNode }) => (
                <a
                  href={href}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="text-primary hover:underline"
                >
                  {children}
                </a>
              ),
              code: ({ children }: { children?: ReactNode }) => (
                <code className="px-1 py-0.5 rounded bg-muted text-xs font-mono">
                  {children}
                </code>
              ),
              blockquote: ({ children }: { children?: ReactNode }) => (
                <blockquote className="border-l-2 border-primary/40 pl-3 my-3 text-sm italic text-foreground/80">
                  {children}
                </blockquote>
              ),
            }}
          >
            {bodyMarkdown}
          </ReactMarkdown>
        </div>
      </div>
    </div>
  );
}
